import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, BehaviorSubject, of } from "rxjs";
import { switchMap, catchError, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AuthService } from "@auth0/auth0-angular";
import { ApiService } from "./api.service";
import { FrontendLoggerService } from "./frontend-logger.service";
import { DbConnectionSettings } from "../models/settings/db-connection/db-connection";

export interface User {
  dbConnectionInfo: DbConnectionSettings;
  id: string;
  name: string;
  email: string;
  role: "admin" | "executive" | "user" | "Admin-unknown";
  avatar?: string;
  company?: string;
  company_logo?: string;
  /** Preferencia de tema persistida por usuario. `null` = sin preferencia guardada. */
  theme?: "light" | "dark" | null;
}

export interface BcUserBindingDto {
  status?: boolean;
  message?: string;
  bcId: number;
  userId: string;
  bcUserId: string;
  accountId: number;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

@Injectable({
  providedIn: "root",
})
export class UserService {

  private apiProfileUrl = environment.apiProfilePhotoUrl;
  // BehaviorSubject to keep current user across the app and notify subscribers on updates
  private currentUserSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private apiService: ApiService,
    private frontendLogger: FrontendLoggerService
  ) {}

  // Obtener perfil del usuario actual
  getCurrentUser(): Observable<User> {
    this.frontendLogger.debug('[UserService] Obteniendo usuario actual', 'UserService.getCurrentUser');
    return this.apiService.get<User>('user/profile').pipe(
      tap((u: User) => {
        try { this.currentUserSubject.next(u); } catch (err) { this.frontendLogger.error('Error updating currentUserSubject: ' + (err || '').toString(), 'UserService.getCurrentUser'); }
      })
    );
  }

  // Verificar si el usuario tiene rol de administrador o ejecutivo
  isAdminOrExecutive(user: User): boolean { 
    return (
      user.role === "admin" ||
      user.role === "executive" ||
      user.role === "Admin-unknown"
    );
  }

  // Verificar si el usuario tiene acceso al dashboard
  hasDashboardAccess(user: User): boolean {
    return this.isAdminOrExecutive(user);
  }

  updateUserSettings(settings: {
    theme: string;
    language: string;
    company_logo_url: string;
  }): Observable<void> {
    this.frontendLogger.warn('[UserService] user/settings no existe en el backend actual. Se conservaran solo cambios locales.', 'UserService.updateUserSettings');
    const current = this.currentUserSubject.value;
    if (current) {
      this.currentUserSubject.next({
        ...current,
        company_logo: settings.company_logo_url || current.company_logo
      });
    }
    return of(undefined);
  }

  // Completar onboarding del usuario
  completeOnboarding(data: any): Observable<any> {
    return this.apiService.post('user/onboarding', data);
  }

  // Validate invite token + email (public endpoint - no auth required)
  validateInviteToken(token: string, email: string): Observable<any> {
    this.frontendLogger.info('[UserService] Validating invite token for ' + email, 'UserService.validateInviteToken');
    return this.apiService.postPublic<any>('admin/invites/validate-token', { token, email });
  }

  // Accept invite / complete onboarding via invite (public endpoint - no auth required)
  acceptInvite(payload: { inviteId: string; token: string; email: string; password: string; firstName: string; lastName: string; language?: string; timeZone?: string; }): Observable<any> {
    this.frontendLogger.info('[UserService] Accepting invite for ' + payload.email, 'UserService.acceptInvite');
    return this.apiService.postPublic<any>('user/onboarding/invite', payload);
  }

  // Guardar perfil del usuario
  saveProfile(formData: {
    name: string;
    company: string;
    jobTitle: string;
    organizationType: string;
    organizationSize: string;
    language?: string;      // nuevo opcional
    timeZone?: string;      // nuevo opcional (backend puede esperarlo como TimeZone / timeZone)
    country?: string;       // nuevo opcional (país)
  }): Observable<any> {
    // Ajustar payload para incluir campos de preferencia si vienen definidos
    const payload: any = { ...formData };
    if (formData.language) {
      // Algunos backends esperan 'Language' capitalizado
      payload.Language = formData.language;
    }
    if (formData.timeZone) {
      // Normalizar posibles variantes
      payload.TimeZone = formData.timeZone;
      payload.timeZone = formData.timeZone;
    }
    if (formData.country) {
      payload.Country = formData.country;
      payload.country = formData.country;
    }
    this.frontendLogger.info('[UserService] Guardando perfil de usuario con payload: ' + JSON.stringify(payload), 'UserService.saveProfile');  
    return this.apiService.put('user/profile', payload).pipe(
      tap(() => {
        try {
          const current = this.currentUserSubject.value;
          if (current) {
            const updated: any = { ...current };
            if (formData.language) {
              updated.language = formData.language;
              updated.Language = formData.language;
            }
            if (formData.timeZone) {
              updated.timeZone = formData.timeZone;
              updated.TimeZone = formData.timeZone;
            }
            this.currentUserSubject.next(updated);
          }
        } catch (err) {
          this.frontendLogger.error('[UserService] Error updating current user after saveProfile: ' + (err as any)?.message, 'UserService.saveProfile');
        }
      })
    );
  }

  // Subir foto de perfil usando el nuevo servicio y manejo de error con catchError
  uploadProfilePhoto(file: File): Observable<{ url: string } | null> {
    return this.authService.getAccessTokenSilently().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.trim()}`
        });
        const formData = new FormData();
        formData.append('file', file);
        var url = this.apiProfileUrl + "/api/profile-photo";
        this.frontendLogger.info("[UserService] Subiendo foto de perfil a:"+ url, "UserService.uploadProfilePhoto");
        return this.http.post<{ url: string }>(url, formData, { headers }).pipe(
          tap((res) => {
            // update current user avatar in memory so other components reflect the change
            try {
              const cu = this.currentUserSubject.value;
              if (cu) {
                const updated = { ...cu, avatar: res?.url || cu.avatar } as User;
                this.currentUserSubject.next(updated);
              }
            } catch (err) {
              this.frontendLogger.error('[UserService] Error updating user avatar in memory: ' + err, "UserService.uploadProfilePhoto");
            }
          })
        );
      }),
      catchError((error) => {
        this.frontendLogger.error('[UserService] Error al subir la foto de perfil:' + error, "UserService.uploadProfilePhoto");

        //retornar error en la promesa
        return new Observable<{ url: string } | null>((observer) => {
          observer.error(error);
          observer.complete();
        });
      })
    );
  }

  /**
   * Obtiene el binding BC del usuario actual
   */
  getBcUserBinding(): Observable<BcUserBindingDto> {
    this.frontendLogger.info('[UserService] Llamando GET /users/bc-user-binding/user', 'UserService.getBcUserBinding');
    return this.apiService.get<BcUserBindingDto>('users/bc-user-binding/user').pipe(
      catchError((err) => {
        this.frontendLogger.error('[UserService] Error en GET /users/bc-user-binding/user: ' + (err?.message || err), 'UserService.getBcUserBinding');
        return new Observable<BcUserBindingDto>((observer) => { observer.error(err); observer.complete(); });
      })
    );
  }

  /**
   * Crea un nuevo binding BC para el usuario actual
   */
  createBcUserBinding(): Observable<BcUserBindingDto> {
    this.frontendLogger.info('[UserService] Llamando POST /users/bc-user-binding', 'UserService.createBcUserBinding');
    return this.apiService.post<BcUserBindingDto>('users/bc-user-binding', {}).pipe(
      catchError((err) => {
        this.frontendLogger.error('[UserService] Error en POST /users/bc-user-binding: ' + (err?.message || err), 'UserService.createBcUserBinding');
        return new Observable<BcUserBindingDto>((observer) => { observer.error(err); observer.complete(); });
      })
    );
  }

  /**
   * Actualiza el binding BC del usuario actual
   */
  updateBcUserBinding(bcid: number): Observable<void> {
    this.frontendLogger.info(`[UserService] Llamando PUT /users/bc-user-binding/${bcid}`, 'UserService.updateBcUserBinding');
    return this.apiService.put<void>(`users/bc-user-binding/${bcid}`, {}).pipe(
      catchError((err) => {
        this.frontendLogger.error(`[UserService] Error en PUT /users/bc-user-binding/${bcid}: ` + (err?.message || err), 'UserService.updateBcUserBinding');
        return new Observable<void>((observer) => { observer.error(err); observer.complete(); });
      })
    );
  }
}
